import React from 'react';
import {
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    PDFViewer,
    PDFDownloadLink
} from '@react-pdf/renderer';
import { Download, Clock } from 'lucide-react';

// ⬅ Ajuste ce chemin selon l'emplacement réel du logo dans ton projet
//import logoSoplast from '../../logo-soplast.png';
import logoSoplast from '../../logoSoplast';


// Bloc légal identique au bon de livraison (logo à gauche, infos à droite)
const COMPANY = {
    addressLine: 'Point E, Rue G X, Rue de Kolda',
    city: 'Dakar-Senegal',
    phone: '+221 33 805 47 50 / 78 911 95 27',
    ninea: '012093530',
    rc: 'SN DKR 2025 B 16191',
    bank: 'Banque BNDE: SN169 00106309301 54',
};



const formatDate = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}/${d.getFullYear()}`;
};

const formatCurrency = (amount) => {
    const rounded = Math.round(Number(amount) || 0);
    return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const styles = StyleSheet.create({
    page: {
        fontFamily: 'Helvetica',
        fontSize: 9,
        paddingTop: 30,
        paddingLeft: 35,
        paddingRight: 35,
        paddingBottom: 40,
        backgroundColor: '#ffffff',
        color: '#000000',
    },

    // ===== En-tête : logo à gauche, bloc légal à droite (identique au BL) =====
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: 10,
        borderBottomWidth: 1.5,
        borderBottomColor: '#000000',
        borderBottomStyle: 'solid',
    },
    headerLeft: { flexDirection: 'row', alignItems: 'center' },
    logo: { width: 60, height: 38, marginRight: 8 },
    companyName: { fontSize: 16, fontFamily: 'Helvetica-Bold', lineHeight: 1.05 },
    headerRight: { textAlign: 'left' },
    headerInfo: { fontSize: 8.5, marginBottom: 1.5 },

    // ===== Bloc client (gauche) + titre (droite) =====
    midSection: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginTop: 14,
        marginBottom: 12,
    },
    clientBlock: { width: '55%' },
    clientLabel: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 4 },
    clientName: { fontSize: 10, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
    clientLine: { fontSize: 9, marginBottom: 1 },
    titleBlock: { width: '40%', alignItems: 'flex-end' },
    docTitle: { fontSize: 22, fontFamily: 'Helvetica-Bold', marginBottom: 6 },
    docMetaRow: { flexDirection: 'row', justifyContent: 'flex-end' },
    docMetaLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginRight: 8 },
    docMetaValue: { fontSize: 9, minWidth: 60, textAlign: 'left' },

    // ===== Tableau =====
    table: {
        borderWidth: 1,
        borderColor: '#000000',
        borderStyle: 'solid',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 0.5,
        borderBottomColor: '#666666',
        alignItems: 'stretch',
        minHeight: 24,
    },
    tableHeaderRow: {
        borderBottomWidth: 1,
        borderBottomColor: '#000000',
        minHeight: 30,
    },
    cell: {
        paddingVertical: 4,
        paddingHorizontal: 3,
        borderRightWidth: 0.5,
        borderRightColor: '#666666',
        justifyContent: 'center',
        backgroundColor: '#dce6f1',
    },
    cellHeaderDark: { backgroundColor: '#1f3864' },
    cellBody: { backgroundColor: '#ffffff' },
    cellLast: { borderRightWidth: 0 },
    headerCellText: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', textAlign: 'center' },
    headerCellTextLight: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', textAlign: 'center', color: '#ffffff' },
    cellText: { fontSize: 8, textAlign: 'left' },
    cellTextCenter: { fontSize: 8, textAlign: 'center' },
    cellTextRight: { fontSize: 8, textAlign: 'right' },

    // Largeurs colonnes (somme = 100%)
    colRef: { width: '7%' },
    colEan: { width: '15%' },
    colLib: { width: '26%' },
    colColis: { width: '11%' },
    colPcb: { width: '7%' },
    colQte: { width: '8%' },
    colPU: { width: '13%' },
    colTotal: { width: '13%' },

    // ===== Totaux =====
    totalsSection: { flexDirection: 'row', justifyContent: 'flex-end' },
    totalsTable: { width: '40%', borderWidth: 1, borderTopWidth: 0, borderColor: '#000000' },
    totalsRow: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#666666', minHeight: 22 },
    totalsRowLast: { borderBottomWidth: 0 },
    totalsLabelCell: { width: '60%', backgroundColor: '#dce6f1', padding: 5, borderRightWidth: 0.5, borderRightColor: '#666666', justifyContent: 'center' },
    totalsValueCell: { width: '40%', padding: 5, justifyContent: 'center' },
    totalsLabel: { fontSize: 9, fontFamily: 'Helvetica-Bold' },
    totalsValue: { fontSize: 9, textAlign: 'right' },

    notes: { marginTop: 14 },
    notesTitle: { fontSize: 9, fontFamily: 'Helvetica-Bold', marginBottom: 3 },
    notesText: { fontSize: 9 },
});

const InvoicePDF = ({ invoice, deliveryNote }) => {
    const order = invoice.order || {};
    const client = invoice.client || {};

    // On facture le bon de livraison : les lignes proviennent du BL
    // (quantités réellement livrées + prix unitaires figés du BL).
    // Repli sur les lignes de la commande pour les anciennes factures sans BL.
    const bl = deliveryNote || invoice.deliveryNote || null;
    const usingBL = !!(bl && Array.isArray(bl.items) && bl.items.length > 0);

    const items = usingBL
        ? bl.items.map((it) => ({
            product: {
                numeroProduit: it.numeroProduit,
                codeBarre: it.codeBarre,
                name: it.productName,
                sku: it.productCode,
                pcb: it.pcb,
            },
            quantity: it.deliveredQuantity,
            unitPrice: it.unitPrice,
            nombreColis: it.nombreColis,
        }))
        : (Array.isArray(order.items) ? order.items : []);

    const totalHT = items.reduce(
        (acc, it) => acc + ((Number(it.unitPrice) || 0) * (Number(it.quantity) || 0)),
        0
    );
    const isTVAApplicable = client.vatApplicable === true || invoice.vatApplicable === true;
    const tva = isTVAApplicable ? totalHT * 0.18 : 0;
    const totalTTC = totalHT + tva;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* En-tête : logo à gauche, bloc légal à droite (comme le BL) */}
                <View style={styles.header}>
                    <View style={styles.headerLeft}>
                        <Image src={logoSoplast} style={styles.logo} />
                        <Text style={styles.companyName}>SENEGAL{'\n'}SOPLAST</Text>
                    </View>
                    <View style={styles.headerRight}>
                        <Text style={styles.headerInfo}>{COMPANY.addressLine}</Text>
                        <Text style={styles.headerInfo}>{COMPANY.city}</Text>
                        <Text style={styles.headerInfo}>Téléphone: {COMPANY.phone}</Text>
                        <Text style={styles.headerInfo}>NINEA: {COMPANY.ninea}</Text>
                        <Text style={styles.headerInfo}>RC: {COMPANY.rc}</Text>
                        <Text style={styles.headerInfo}>{COMPANY.bank}</Text>
                    </View>
                </View>

                {/* Bloc client (gauche) + titre FACTURE (droite) */}
                <View style={styles.midSection}>
                    <View style={styles.clientBlock}>
                        <Text style={styles.clientLabel}>CLIENT</Text>
                        <Text style={styles.clientName}>{client.name}</Text>
                        {client.address && (
                            <Text style={styles.clientLine}>Adresse: {client.address}</Text>
                        )}
                        <Text style={styles.clientLine}>N° de commande: {order.referenceClient || ''}</Text>
                    </View>

                    <View style={styles.titleBlock}>
                        <Text style={styles.docTitle}>FACTURE</Text>
                        <View style={styles.docMetaRow}>
                            <Text style={styles.docMetaLabel}>N°:</Text>
                            <Text style={styles.docMetaValue}>{invoice.invoiceNumber}</Text>
                        </View>
                        <View style={styles.docMetaRow}>
                            <Text style={styles.docMetaLabel}>Date:</Text>
                            <Text style={styles.docMetaValue}>{formatDate(invoice.issueDate)}</Text>
                        </View>
                    </View>
                </View>

                {/* Tableau */}
                <View style={styles.table}>
                    <View style={[styles.tableRow, styles.tableHeaderRow]}>
                        <View style={[styles.cell, styles.colRef]}>
                            <Text style={styles.headerCellText}>Ref</Text>
                        </View>
                        <View style={[styles.cell, styles.colEan]}>
                            <Text style={styles.headerCellText}>EAN principal</Text>
                        </View>
                        <View style={[styles.cell, styles.colLib]}>
                            <Text style={styles.headerCellText}>Libellé</Text>
                        </View>
                        <View style={[styles.cell, styles.cellHeaderDark, styles.colColis]}>
                            <Text style={styles.headerCellTextLight}>Nombre de colis</Text>
                        </View>
                        <View style={[styles.cell, styles.cellHeaderDark, styles.colPcb]}>
                            <Text style={styles.headerCellTextLight}>PCB</Text>
                        </View>
                        <View style={[styles.cell, styles.cellHeaderDark, styles.colQte]}>
                            <Text style={styles.headerCellTextLight}>Qté</Text>
                        </View>
                        <View style={[styles.cell, styles.colPU]}>
                            <Text style={styles.headerCellText}>Prix Unitaire (FCFA)</Text>
                        </View>
                        <View style={[styles.cell, styles.cellLast, styles.colTotal]}>
                            <Text style={styles.headerCellText}>Prix Total (FCFA)</Text>
                        </View>
                    </View>

                    {items.length > 0 ? (
                        items.map((item, index) => {
                            const product = item.product || {};
                            const qte = Number(item.quantity) || 0;
                            const pcb = product.pcb ?? '';
                            const colis = item.nombreColis ?? (Number(pcb) > 0 ? Math.floor(qte / Number(pcb)) : '');
                            const lineTotal = (Number(item.unitPrice) || 0) * qte;
                            return (
                                <View key={index} style={styles.tableRow}>
                                    <View style={[styles.cell, styles.cellBody, styles.colRef]}>
                                        <Text style={styles.cellTextCenter}>{product.numeroProduit}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colEan]}>
                                        <Text style={styles.cellTextCenter}>{product.codeBarre}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colLib]}>
                                        <Text style={styles.cellText}>{product.name+'-'+product.sku}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colColis]}>
                                        <Text style={styles.cellTextCenter}>{colis}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colPcb]}>
                                        <Text style={styles.cellTextCenter}>{pcb}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colQte]}>
                                        <Text style={styles.cellTextCenter}>{qte}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.colPU]}>
                                        <Text style={styles.cellTextRight}>{formatCurrency(item.unitPrice)}</Text>
                                    </View>
                                    <View style={[styles.cell, styles.cellBody, styles.cellLast, styles.colTotal]}>
                                        <Text style={styles.cellTextRight}>{formatCurrency(lineTotal)}</Text>
                                    </View>
                                </View>
                            );
                        })
                    ) : (
                        <View style={styles.tableRow}>
                            <View style={[styles.cellBody, { width: '100%', padding: 6 }]}>
                                <Text style={styles.cellText}>Aucun élément dans cette facture</Text>
                            </View>
                        </View>
                    )}
                </View>

                {/* Totaux */}
                <View style={styles.totalsSection}>
                    <View style={styles.totalsTable}>
                        <View style={styles.totalsRow}>
                            <View style={styles.totalsLabelCell}><Text style={styles.totalsLabel}>Montant HT</Text></View>
                            <View style={styles.totalsValueCell}><Text style={styles.totalsValue}>{formatCurrency(totalHT)}</Text></View>
                        </View>
                        <View style={styles.totalsRow}>
                            <View style={styles.totalsLabelCell}><Text style={styles.totalsLabel}>TVA 18%</Text></View>
                            <View style={styles.totalsValueCell}><Text style={styles.totalsValue}>{formatCurrency(tva)}</Text></View>
                        </View>
                        <View style={[styles.totalsRow, styles.totalsRowLast]}>
                            <View style={styles.totalsLabelCell}><Text style={styles.totalsLabel}>Montant TTC</Text></View>
                            <View style={styles.totalsValueCell}><Text style={styles.totalsValue}>{formatCurrency(totalTTC)}</Text></View>
                        </View>
                    </View>
                </View>

                {invoice.notes && (
                    <View style={styles.notes}>
                        <Text style={styles.notesTitle}>Notes:</Text>
                        <Text style={styles.notesText}>{invoice.notes}</Text>
                    </View>
                )}
            </Page>
        </Document>
    );
};

const InvoicePDFViewer = ({ invoice, company, deliveryNote }) => (
    <PDFViewer width="100%" height={600} style={{ border: 'none' }}>
        <InvoicePDF invoice={invoice} company={company} deliveryNote={deliveryNote} />
    </PDFViewer>
);

const InvoicePDFDownloadLink = ({ invoice, company, deliveryNote }) => (
    <PDFDownloadLink
        document={<InvoicePDF invoice={invoice} company={company} deliveryNote={deliveryNote} />}
        fileName={`facture_${invoice.invoiceNumber}.pdf`}
        style={{
            textDecoration: 'none',
            padding: '0.25rem 0.5rem',
            backgroundColor: 'transparent',
            color: '#2563eb',
            border: '1px solid #2563eb',
            borderRadius: '0.375rem',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            lineHeight: '1.5',
            minHeight: '31px',
        }}
        className="me-1"
    >
        {({ blob, url, loading, error }) =>
            loading ? <Clock size={16} /> : <Download size={16} />
        }
    </PDFDownloadLink>
);

const GenererPdfFacture = ({ invoice, company, deliveryNote, showPreview = false, onDownload = null }) => {
    if (onDownload) {
        return (
            <PDFDownloadLink
                document={<InvoicePDF invoice={invoice} company={company} deliveryNote={deliveryNote} />}
                fileName={`facture_${invoice.invoiceNumber}.pdf`}
                style={{ display: 'none' }}
            >
                {({ blob, url, loading, error }) => {
                    if (!loading && !error && blob) {
                        onDownload(blob);
                    } else if (error) {
                        console.error("Erreur lors de la génération du PDF:", error);
                    }
                    return null;
                }}
            </PDFDownloadLink>
        );
    }

    return (
        <div>
            {showPreview ? (
                <InvoicePDFViewer invoice={invoice} company={company} deliveryNote={deliveryNote} />
            ) : (
                <InvoicePDFDownloadLink invoice={invoice} company={company} deliveryNote={deliveryNote} />
            )}
        </div>
    );
};

export default GenererPdfFacture;